import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, type DashboardData, type DashboardRange, type BranchRef } from '../lib/api'
import { LineChart, HBarChart } from '../components/charts'
import { GetStarted } from '../components/GetStarted'
import { formatMoney, formatDate, formatDateTime } from '../lib/format'

const RANGES: { key: DashboardRange; label: string }[] = [
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
  { key: 'custom', label: 'Custom' },
]
const presetNote: Record<string, string> = {
  week: 'Last 7 days',
  month: 'Last 30 days',
  year: 'Last 12 months',
}

function isoDaysAgo(days: number): string {
  const d = new Date(Date.now() - days * 86_400_000)
  return d.toISOString().slice(0, 10)
}

export function DashboardPage() {
  const [range, setRange] = useState<DashboardRange>('month')
  const [from, setFrom] = useState(isoDaysAgo(90))
  const [to, setTo] = useState(isoDaysAgo(0))
  const [applied, setApplied] = useState<{ from: string; to: string } | null>(null)
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [query, setQuery] = useState('')
  const [branches, setBranches] = useState<BranchRef[]>([])
  const [branch, setBranch] = useState('')
  const navigate = useNavigate()

  const awaitingDates = range === 'custom' && !applied

  useEffect(() => {
    api
      .listBranches()
      .then((b) => setBranches(b.branches.filter((x) => x.active === 1)))
      .catch(() => setBranches([]))
  }, [])

  useEffect(() => {
    if (awaitingDates) {
      setLoading(false)
      return
    }
    let active = true
    setLoading(true)
    setError(false)
    api
      .getDashboard(range, applied?.from, applied?.to, branch || undefined)
      .then((d) => active && setData(d))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [range, applied, awaitingDates, branch])

  function pickRange(next: DashboardRange) {
    setRange(next)
    if (next === 'custom') setApplied(null)
  }

  function applyCustom(e: FormEvent) {
    e.preventDefault()
    if (!from || !to || from > to) return
    setApplied({ from, to })
  }

  function onSearch(e: FormEvent) {
    e.preventDefault()
    const q = query.trim()
    if (!q) return
    navigate(`/app/search?q=${encodeURIComponent(q)}`)
  }

  // Bucket keys are YYYY-MM (monthly) or YYYY-MM-DD (daily).
  function fmtX(key: string): string {
    if (key.length === 7) {
      return new Date(`${key}-01T00:00:00`).toLocaleDateString(undefined, { month: 'short' })
    }
    return new Date(`${key}T00:00:00`).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })
  }

  // Reported-date window the tiles hand to the case lists (+ branch drill-down).
  const win = data
    ? `openedFrom=${data.from}&openedTo=${data.to}${branch ? `&branch=${branch}` : ''}`
    : ''

  const note =
    range === 'custom'
      ? applied
        ? `${formatDate(applied.from)} – ${formatDate(applied.to)}`
        : 'Choose a start and end date'
      : presetNote[range]

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <div className="page-title">Dashboard</div>
          <p className="page-sub" style={{ margin: 0 }}>{note}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {branches.length > 1 && (
            <select
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              aria-label="Filter by branch"
              style={{ padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 8, font: 'inherit' }}
            >
              <option value="">All branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}
          <div className="seg">
            {RANGES.map((r) => (
              <button
                key={r.key}
                className={r.key === range ? 'seg-btn active' : 'seg-btn'}
                onClick={() => pickRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <GetStarted />
      </div>

      {/* Quick search */}
      <form className="quick-search" onSubmit={onSearch}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search cases — number, customer, address, technician…"
          aria-label="Search cases"
        />
        <button className="btn btn-primary" type="submit">Search</button>
      </form>

      {range === 'custom' && (
        <form className="card range-picker" onSubmit={applyCustom}>
          <div className="field" style={{ margin: 0 }}>
            <label>From</label>
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} required />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>To</label>
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} required />
          </div>
          <button className="btn btn-primary" type="submit">Apply</button>
        </form>
      )}

      {awaitingDates ? (
        <div className="card" style={{ marginTop: 16, color: 'var(--gray)' }}>
          Pick a start and end date, then choose Apply.
        </div>
      ) : loading ? (
        <div className="card" style={{ marginTop: 16, color: 'var(--gray)' }}>Loading…</div>
      ) : error || !data ? (
        <div className="card" style={{ marginTop: 16, color: 'var(--gray)' }}>
          Couldn't load the dashboard.{' '}
          <button className="link-btn" onClick={() => location.reload()}>Retry</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16, marginTop: 16 }}>
          {/* Stat tiles — each opens the cases behind the number, carrying this
              dashboard's reported-date window so the count matches the list. */}
          <div className="stat-row">
            <Stat label="Total Damages" value={String(data.totalDamages)} to={`/app/cases?${win}`} />
            <Stat label="Open" value={String(data.statusCounts.open)} accent="var(--status-open)" to={`/app/open?${win}`} />
            <Stat label="In Progress" value={String(data.statusCounts.in_progress)} accent="var(--status-in-progress)" to={`/app/in-progress?${win}`} />
            <Stat label="Closed" value={String(data.statusCounts.closed)} accent="var(--status-closed)" to={`/app/closed?${win}`} />
            <Stat label="Repair Cost" value={formatMoney(data.totalCost)} to={`/app/cases?${win}`} />
          </div>

          {/* Damages over time */}
          <div className="card">
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Damages over time</div>
            <p className="page-sub" style={{ marginBottom: 8 }}>New damage reports, {note.toLowerCase()}</p>
            <LineChart
              data={data.timeseries.map((t) => ({ key: t.bucket, value: t.count }))}
              formatX={fmtX}
            />
          </div>

          {/* Top techs + subs */}
          <div className="two-col">
            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Top technicians</div>
              <HBarChart
                data={data.topTechnicians}
                empty="No submissions in this period."
                onSelect={(id) => navigate(`/app/technicians/${id}`)}
              />
            </div>
            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Most-used subcontractors</div>
              <HBarChart
                data={data.topSubcontractors}
                color="var(--navy)"
                empty="No assignments in this period."
                onSelect={(id) => navigate(`/app/subcontractors/${id}`)}
              />
            </div>
          </div>

          {/* Recent activity */}
          <div className="card">
            <div style={{ fontWeight: 700, marginBottom: 12 }}>Recent activity</div>
            {data.recentActivity.length === 0 ? (
              <p style={{ color: 'var(--gray)', fontSize: 14 }}>No recent activity.</p>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {data.recentActivity.map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 14 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--blue)', flexShrink: 0, transform: 'translateY(-1px)' }} />
                    <div style={{ flex: 1 }}>
                      {a.action}
                      {a.actor ? <span style={{ color: 'var(--gray)' }}> · {a.actor}</span> : null}
                    </div>
                    <div style={{ color: 'var(--gray)', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {formatDateTime(a.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  accent,
  to,
}: {
  label: string
  value: string
  accent?: string
  to?: string
}) {
  const body = (
    <>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={accent ? { color: accent } : undefined}>{value}</div>
    </>
  )
  if (!to) return <div className="card stat">{body}</div>
  return (
    <Link to={to} className="card stat stat-link">
      {body}
    </Link>
  )
}
